import React from "react";
import { useHistory } from "react-router-dom";
import { Dropdown, Icon, Label } from "semantic-ui-react";
import displayName from "utils/displayName";
import { getTimeBetweenInSec } from "utils/dateUtils";
import moment from "moment";
import { HelpFolderDropDownItem } from "components/Others/HelpFolderDropDownItem";
import { getFormattedTimeFromMinutes } from "utils/dateUtils";
import { getTimeBeforeSamplingAnalysis, getTimeBeforeResult, getTimeBeforeNextTube } from "utils/folder";

const DropDownItemFolder = ({
	folder,
	fastresult,
	folderHelp,
	focusFolderManager,
	acceptSupportFolderHelp,
	doneFolderHelp,
	cancel,
	isNurse,
	goToResult,
	goToAliquot,
	goToTubes,
	aliquot,
	sampling,
	beforeAnalysis,
	user,
	openPreregisterModal,
}) => {
	const styles = {
		folder: {
			fontSize: "0.8em",
			textAlign: "left",
			lineHeight: "1.5em",
			width: "100%",
			boxShadow: "#00808038 1px 1px",
		},
	};
	let history = useHistory();

	const goToFolderManager = (folderId) => {
		focusFolderManager([folderId]);
		if (goToResult) goToResult();
		if (goToAliquot) goToAliquot();
		if (goToTubes) goToTubes();
		history.push("/home/foldermanager");
	};

	const timingBeforeResult = (tube) =>
		tube ? Math.floor(Math.abs(getTimeBetweenInSec(moment(tube.sampling_time).add(tube.properties.timer_before_result, "m")) / 60)) : "?";

	const getFastResultInfoToDisplay = (folder) => {
		if (
			folder.prescriptions &&
			folder.prescriptions.length > 0 &&
			folder.prescriptions.some((p) => p.tubes && p.tubes.some((t) => t.properties.timer_before_result > 0))
		) {
			const prescri = folder.prescriptions.find((p) => p.tubes.some((t) => t.properties.timer_before_result > 0));
			const fastResultTube = prescri.tubes.filter((t) => t.properties.timer_before_result > 0)[0];
			const timeToRead = getTimeBeforeResult(folder);
			return (
				<div style={styles.folder}>
					<Label as="div" color={timeToRead > 0 ? "grey" : timeToRead < -900 ? "red" : "green"}>
						<Icon name="calendar check outline" /> En attente de résultat
						<Label.Detail>
							{timeToRead > 0 ? (
								<>
									<Icon size="big" name="wait" />
									Attendre ({timingBeforeResult(fastResultTube)} min){" "}
								</>
							) : timeToRead < -900 ? (
								<>
									<Icon size="big" name="warning circle" />
									Lecture en retard ({timingBeforeResult(fastResultTube)} min){" "}
								</>
							) : (
								<>
									<Icon size="big" name="check circle" />
									Test à lire
								</>
							)}
						</Label.Detail>
					</Label>

					<div style={{ marginTop: "10px" }}>
						<Icon size="big" name="folder open outline" />
						Dossier {folder.code}
					</div>
					<div>{displayName(folder.patient)} </div>
					<div>Position portoir : {fastResultTube?.provider_barcode}</div>
				</div>
			);
		}
	};

	const getFolderInfoToDisplay = (folder) => {
		const isFastLinePatient = folder?.patient?.first_name === "Register" && folder?.patient?.birth_name === "Line";
		return (
			<div style={styles.folder} key={"folder-" + folder.id}>
				<div>
					<Icon size="big" name="folder open outline" />
					Dossier {folder.code}
				</div>
				<div>{isFastLinePatient ? "Patient à associer" : displayName(folder.patient)} </div>
			</div>
		);
	};

	const getSamplingInfoToDisplay = (folder) => {
		const nextTubeToSample = folder.prescriptions.flatMap((p) => p.tubes.filter((t) => !t.sampling_time && t.properties.timer > 0));
		let timeBeforeSampling = getTimeBeforeNextTube(folder);
		return (
			<div style={styles.folder} key={"waiting-time-tube-" + folder.id}>
				<div>
					<Label color={timeBeforeSampling > 0 ? "grey" : "green"}>
						<Icon name="lab" /> Délai entre prélèvements
						<Label.Detail>
							{timeBeforeSampling > 0 ? (
								<>
									<Icon size="big" name="wait" />
									Attendre ({Math.floor(Math.abs(getTimeBeforeNextTube(folder) / 60))} min){" "}
								</>
							) : (
								<>
									<Icon size="big" name="check circle" />
									Patient à prélever
								</>
							)}
						</Label.Detail>
					</Label>
				</div>
				<div>
					<Icon size="big" name="folder open outline" />
					Dossier {folder.code}
				</div>
				<div>{displayName(folder.patient)} </div>
				<div>
					Tubes:{" "}
					{nextTubeToSample.map((t) => (
						<Label key={t.id} size="tiny">
							{t.properties.label}
							<Label.Detail>{t.properties.timer} min</Label.Detail>
						</Label>
					))}
				</div>
			</div>
		);
	};

	const getWaitingTimeBeforeAnalysis = (folder) => {
		const timeBeforeSampling = getTimeBeforeSamplingAnalysis(folder);
		return (
			<div style={styles.folder} key={"waiting-time-before-" + folder.id}>
				<div>
					<Label color={timeBeforeSampling > 0 ? "grey" : "green"}>
						<Icon name="stopwatch" /> Repos avant prélèvement
						<Label.Detail>
							{timeBeforeSampling > 0 ? (
								<>
									<Icon size="big" name="wait" />
									Attendre ({Math.floor(Math.abs(timeBeforeSampling / 60))} min){" "}
								</>
							) : (
								<>
									<Icon size="big" name="check circle" />
									Patient à prélever
								</>
							)}
						</Label.Detail>
					</Label>
				</div>
				<div>
					<Icon size="big" name="folder open outline" />
					Dossier {folder.code}
				</div>
				<div>{displayName(folder.patient)} </div>
				<div>
					Analyses :{" "}
					{folder.prescriptions
						.flatMap((p) => p.analyses.filter((a) => a.properties?.waiting_time_before_sampling > 0))
						.map((ana) => (
							<Label key={ana.id} size="tiny">
								{ana.properties.code}
								<Label.Detail>{ana.properties.waiting_time_before_sampling} min</Label.Detail>
							</Label>
						))}
				</div>
			</div>
		);
	};

	const getAliquotInfoToDisplay = (folder) => {
		const primaryDelay = getPrimaryMinDelay(folder);
		const delay = getDelay(folder.prescriptions.flatMap((p) => p.tubes && p.tubes.filter((t) => t.provider_barcode)));
		return (
			<div style={styles.folder} key={"aliquot-" + folder.id}>
				<Label as="div" color={!delay || primaryDelay > delay ? "green" : "red"} ribbon>
					En attente
				</Label>
				<div>
					<Icon size="big" name="lab" />
					{folder.prescriptions
						.flatMap((p) => p.tubes && p.tubes.filter((t) => !t.provider_barcode && t.properties.aliquot).map((t) => t.properties.label))
						.join(", ")}
				</div>
				<div>
					<Icon size="big" name="folder" />
					Dossier {folder.code} - {displayName(folder.patient)}
				</div>
				{delay > 0 && (
					<div>
						{" "}
						<Icon size="big" name="wait" />
						{primaryDelay > delay ? (
							<span>Tube primaire prélevé il y a {getFormattedTimeFromMinutes(delay)}</span>
						) : (
							<span>En retard depuis {getFormattedTimeFromMinutes(delay - primaryDelay)} </span>
						)}{" "}
					</div>
				)}
			</div>
		);
	};

	const getPrimaryMinDelay = (folder) => {
		return (
			Math.min(
				...folder.prescriptions
					.flatMap((p) => p.analyses.filter((a) => a.properties.pretreatment_stability > 0))
					.map((a) => a.properties.pretreatment_stability)
			) || 0
		);
	};
	const getDelay = (tubesPrimary) => {
		const min = tubesPrimary.map((t) => Math.min(getTimeBetweenInSec(moment(t.sampling_time)) / 60));
		return Math.floor(Math.abs(min[0]));
	};

	const getFolderHelpToDisplay = (folderHelp) => {
		return (
			<HelpFolderDropDownItem
				key={folderHelp.id}
				folderHelp={folderHelp}
				isNurse={isNurse}
				open={(folderHelp) => {
					if (folderHelp.fromPreregister && folderHelp.folder.preregister && openPreregisterModal)
						openPreregisterModal(folderHelp.folder.preregister.qr_code);
					else goToFolderManager(folderHelp.folder.id);
				}}
				openAndDone={(folderHelp) => {
					doneFolderHelp(folderHelp.folder.id).then(() => {
						if (folderHelp.fromPreregister && folderHelp.folder.preregister && openPreregisterModal)
							openPreregisterModal(folderHelp.folder.preregister.qr_code);
						else goToFolderManager(folderHelp.folder.id);
					});
				}}
				cancel={cancel}
				support={(folderHelp) => {
					acceptSupportFolderHelp(folderHelp.folder.id).then(() => goToFolderManager(folderHelp.folder.id));
				}}
				display={true}
				user={user}
			/>
		);
	};

	return (
		<Dropdown.Item
			key={`ddi-${folder.id}`}
			onClick={(e) => {
				if (e !== undefined) e.preventDefault();
				if (!folderHelp) {
					goToFolderManager(folder.id);
				}
			}}
			children={
				fastresult
					? getFastResultInfoToDisplay(folder)
					: folderHelp
					? getFolderHelpToDisplay(folder)
					: aliquot
					? getAliquotInfoToDisplay(folder)
					: sampling
					? getSamplingInfoToDisplay(folder)
					: beforeAnalysis
					? getWaitingTimeBeforeAnalysis(folder)
					: getFolderInfoToDisplay(folder)
			}
		/>
	);
};

export default DropDownItemFolder;
